import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Table, Button, Space, Input, Tag, Select, Typography, Row, Col, Divider, theme } from 'antd';
import { FileTextOutlined, SearchOutlined, CopyOutlined, ForkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { listPublicDocumentTemplates, getDocumentTemplateDetails } from '../../actions/documentTemplateActions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import debounce from 'lodash/debounce';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Helper to format numbers as 1.2K, 3.4M, etc.
function formatCount(num) {
  if (num == null) return 0;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1) + 'K';
  return num;
}

const PublicTemplateBrowser = ({ visible, onClose, onSelectTemplate }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const [filterState, setFilterState] = useState({
    filter: 'name',
    query: '',
    page: 1,
    limit: 10,
    sortBy: 'derived',
    sortOrder: 'DESC',
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const searchInput = useRef(null);
  const [searchInputValue, setSearchInputValue] = useState('');

  const publicList = useSelector(state => state.documentTemplatePublicList);
  const { loading, error, documentTemplates = [], totalPages = 1, totalCount = 0 } = publicList;

  const templateDetails = useSelector(state => state.documentTemplateDetails);
  const { loading: loadingDetails, documentTemplate } = templateDetails;

  // Extract unique languages and specialties for filter dropdowns
  const languages = Array.from(new Set(documentTemplates.map(t => t.language).filter(Boolean)));
  const specialties = Array.from(new Set(documentTemplates.map(t => t.specialty).filter(Boolean)));

  const languageOptions = [
    'en',
    'fr',
    'de',
    'es',
    ...languages.filter(l => !['en', 'fr', 'de', 'es'].includes(l))
  ];

  const debouncedFilterChange = useRef(
    debounce((nextFilter) => {
      setFilterState(prev => ({ ...prev, ...nextFilter }));
    }, 500)
  ).current;

  useEffect(() => {
    if (visible) {
      dispatch(listPublicDocumentTemplates({
        page: filterState.page,
        limit: filterState.limit,
        filter: filterState.filter || undefined,
        query: filterState.query || undefined,
        sortBy: filterState.sortBy,
        sortOrder: filterState.sortOrder,
      }));
    }
  }, [dispatch, filterState, visible]);

  useEffect(() => {
    return () => {
      debouncedFilterChange.clear && debouncedFilterChange.clear();
    };
  }, [debouncedFilterChange]);

  useEffect(() => {
    // Keep input in sync with filterState.query if filter changes from outside
    if (filterState.filter === 'name') {
      setSearchInputValue(filterState.query || '');
    }
  }, [filterState.filter, filterState.query]);

  const handleViewDetails = (template) => {
    setSelectedTemplate(template);
    dispatch(getDocumentTemplateDetails(template.id));
    setDetailsDrawerVisible(true);
  };

  const handleUseTemplate = (template) => {
    onSelectTemplate(template);
    setDetailsDrawerVisible(false);
    onClose();
  };

  const handleFilterChange = (value, type) => {
    if (type === 'name') {
      setSearchInputValue(value); // always update input immediately
      // Only trigger backend search if empty or >= 3 chars
      if (!value || value.length >= 2) {
        debouncedFilterChange({ query: value, page: 1, filter: 'name' });
      } else {
        debouncedFilterChange.cancel && debouncedFilterChange.cancel();
      }
    } else {
      debouncedFilterChange({ query: value, page: 1, filter: type });
    }
  };

  const handleSortChange = (value) => {
    const [newSortBy, newSortOrder] = value.split('-');
    debouncedFilterChange({ sortBy: newSortBy, sortOrder: newSortOrder, page: 1 });
  };

  const columns = [
    {
      title: t('Template'),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space direction="vertical" size={4} style={{ flex: 1 }}>
            <Space size={8} align="center">
              <FileTextOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
              <Text strong style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => handleViewDetails(record)}>
                {record.name}
              </Text>
            </Space>
            <Space size={4}>
              <Tag icon={<ForkOutlined />} color="geekblue">
                {formatCount(record.derivedTemplateCount || 0)}
              </Tag>
              {record.specialty && <Tag color="purple">{record.specialty}</Tag>}
              {record.language && <Tag color="blue">{record.language}</Tag>}
            </Space>
          </Space>
          <Button 
            type="primary" 
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleUseTemplate(record)}
            style={{ marginLeft: 16 }}
          >
            {t('Use this template')}
          </Button>
        </div>
      ),
    }
  ];

  return (
    <Drawer
      title={
        <Space>
          <FileTextOutlined />
          {t('Browse Public Templates')}
        </Space>
      }
      placement="right"
      width={720}
      onClose={onClose}
      open={visible}
      styles={{
        body: {
          padding: '0 24px 24px 24px'
        }
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div style={{ padding: '16px', borderRadius: '8px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                style={{ width: 120 }}
                placeholder={t('Filter by')}
                onChange={(value) => debouncedFilterChange({ filter: value, page: 1 })}
                value={filterState.filter}
                allowClear
              >
                <Option value="language">{t('Language')}</Option>
                <Option value="specialty">{t('Specialty')}</Option>
                <Option value="name">{t('Name')}</Option>
              </Select>
              {filterState.filter === 'language' && (
                <Select
                  style={{ flex: 1 }}
                  placeholder={t('Select language')}
                  onChange={(value) => handleFilterChange(value, 'language')}
                  allowClear
                >
                  {languageOptions.map(l => <Option key={l} value={l}>{l}</Option>)}
                </Select>
              )}
              {filterState.filter === 'specialty' && (
                <Select
                  style={{ flex: 1 }}
                  placeholder={t('Select specialty')}
                  onChange={(value) => handleFilterChange(value, 'specialty')}
                  allowClear
                >
                  {specialties.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              )}
              {filterState.filter === 'name' && (
                <Input
                  placeholder={t('Search by name')}
                  value={searchInputValue}
                  onChange={e => {
                    const value = e.target.value;
                    handleFilterChange(value, 'name');
                  }}
                  allowClear
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                />
              )}
            </Space.Compact>
            <Select
              style={{ width: '100%' }}
              placeholder={t('Sort by')}
              onChange={handleSortChange}
              value={`${filterState.sortBy}-${filterState.sortOrder}`}
            >
              <Option value="derived-DESC">{t('Most popular')}</Option>
              <Option value="derived-ASC">{t('Least popular')}</Option>
              <Option value="name-ASC">{t('Name (A-Z)')}</Option>
              <Option value="name-DESC">{t('Name (Z-A)')}</Option>
              <Option value="created-DESC">{t('Newest')}</Option>
              <Option value="created-ASC">{t('Oldest')}</Option>
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={documentTemplates.map(t => ({ ...t, key: t.id }))}
          loading={loading}
          pagination={{
            current: filterState.page,
            pageSize: filterState.limit,
            total: totalCount,
            onChange: (page, pageSize) => debouncedFilterChange({ page, limit: pageSize }),
            showSizeChanger: true,
          }}
          size="middle"
          showHeader={false}
        />

        <Drawer
          title={
            <Space>
              <FileTextOutlined />
              {selectedTemplate?.name}
            </Space>
          }
          placement="right"
          width={480}
          onClose={() => setDetailsDrawerVisible(false)}
          open={detailsDrawerVisible}
          styles={{
            body: {
              padding: '24px'
            }
          }}
        >
          {loadingDetails ? (
            <div>Loading...</div>
          ) : documentTemplate && (
            <Space direction="vertical" style={{ width: '100%' }} size={24}>
              {documentTemplate.rawContent && (
                <div>
                  <Text type="secondary">{t('Content Preview')}</Text>
                  <div style={{ 
                    marginTop: 8,
                    padding: '16px',
                    borderRadius: '8px',
                    background: token.colorBgContainer,
                    color: token.colorText,
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {documentTemplate.rawContent}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {documentTemplate.structure && (
                <div>
                  <Text type="secondary">{t('Structure')}</Text>
                  <Paragraph style={{ marginTop: 8 }}>{documentTemplate.structure}</Paragraph>
                </div>
              )}

              {documentTemplate.exampleNote && (
                <div>
                  <Text type="secondary">{t('Example Note')}</Text>
                  <Paragraph style={{ marginTop: 8 }}>{documentTemplate.exampleNote}</Paragraph>
                </div>
              )}

              {documentTemplate.howToUse && (
                <div>
                  <Text type="secondary">{t('How To Use')}</Text>
                  <Paragraph style={{ marginTop: 8 }}>{documentTemplate.howToUse}</Paragraph>
                </div>
              )}

              <Button 
                type="primary" 
                onClick={() => handleUseTemplate(documentTemplate)}
                block
                size="large"
                icon={<CopyOutlined />}
              >
                {t('Use this template')}
              </Button>
            </Space>
          )}
        </Drawer>
      </Space>
    </Drawer>
  );
};

export default PublicTemplateBrowser; 